import React, { useState, useEffect } from 'react';
import { Star, ExternalLink } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * ReviewsWidget — Avis clients réutilisable
 * Compact: juste la note + nombre
 * Full: cards d'avis
 * google: bouton laisser un avis Google
 */
export default function ReviewsWidget({ 
  compact = false, 
  showGoogleLink = true,
  maxReviews = 3,
  googleUrl = null,
  className = '' 
}) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/avis?public=true&limit=${maxReviews}`)
      .then(r => r.json())
      .then(d => setReviews(d.reviews || []))
      .catch((err) => { /* eslint-disable-line no-empty */ })
      .finally(() => setLoading(false));

    if (showGoogleLink) {
      fetch(`${BACKEND}/api/parametres`)
        .then(r => r.json())
        .then(d => setSettings(d))
        .catch((err) => { /* eslint-disable-line no-empty */ });
    }
  }, [maxReviews, showGoogleLink]);

  const googleReviewUrl = googleUrl || settings?.google_review_url || 'https://share.google/hZSHglw36RKeQgqnp';
  const avgNote = reviews.length > 0 
    ? (reviews.reduce((s, r) => s + r.note, 0) / reviews.length).toFixed(1) 
    : '4,6';

  // COMPACT: just stars + count + optional google link
  if (compact) {
    return (
      <div className={`flex flex-wrap items-center gap-3 ${className}`}>
        <div className="flex items-center gap-1">
          {[1,2,3,4,5].map(s => <Star key={s} size={13} fill="var(--gold)" color="var(--gold)" />)}
          <span className="text-sm font-bold price-gold ml-1">{avgNote}</span>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({reviews.length}+ avis)</span>
        </div>
        {showGoogleLink && googleReviewUrl && (
          <a href={googleReviewUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg"
            style={{ background: 'var(--teal-mist)', color: '#0B4B4E', border: '1px solid rgba(93,188,191,0.25)' }}>
            <Star size={11} fill="var(--gold)" color="var(--gold)" />
            Laisser un avis Google
            <ExternalLink size={10} />
          </a>
        )}
      </div>
    );
  }

  // FULL: review cards
  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {[1,2,3,4,5].map(s => <Star key={s} size={16} fill="var(--gold)" color="var(--gold)" />)}
          <span className="font-bold price-gold">{avgNote} / 5</span>
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>— {reviews.length}+ avis vérifiés</span>
        </div>
        {showGoogleLink && googleReviewUrl && (
          <a href={googleReviewUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl"
            style={{ background: 'var(--teal-mist)', color: '#0B4B4E', border: '1px solid rgba(93,188,191,0.3)' }}>
            <Star size={11} fill="var(--gold)" color="var(--gold)" />
            Laisser un avis Google
            <ExternalLink size={11} />
          </a>
        )}
      </div>

      {/* Review cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[1,2,3].map(i => <div key={`review-${i}`} className="h-24 rounded-2xl animate-pulse" style={{ background: 'var(--border)' }} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {reviews.map(r => (
            <div key={r.id} className="card-premium p-4">
              <div className="flex items-center gap-0.5 mb-2">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={12} fill={s <= r.note ? 'var(--gold)' : 'transparent'} color="var(--gold)" />
                ))}
              </div>
              <p className="text-xs italic mb-2" style={{ color: 'var(--text-muted)', lineHeight: '1.65' }}>
                “{r.commentaire}”
              </p>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>— {r.client_nom}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
